import {LocationType} from '@/enums/locationType';

/**
 * @name CampaignPaidGigBriefItem
 * @type campaign_paid_gig_brief_items
 */
export type CampaignPaidGigBriefItem = {
  id?: string;
  clientBriefTemplateId?: string;
  contentHtml: string | null;
  highlighted?: string;
  title: string;
  weight?: number;
};

/**
 * @name CampaignTrackingUrl
 * @type campaign_tracking_urls
 */
export type CampaignTrackingUrl = {
  id?: string;
  label: string;
  url: string;
};

/**
 * @name CampaignBriefDeliverableItem
 * @type campaign_brief_deliverable_items
 */
export type CampaignBriefDeliverableItem = {
  id: string;
  weight?: number;
  network?: string;
  itemType: string;
  description: string;
};

/**
 * @name CampaignBriefCaptionGuideline
 * @type campaign_brief_caption_guidelines
 */
export type CampaignBriefCaptionGuideline = {
  campaignId?: string;
  id: string;
  kind: 'caption' | 'mention' | 'hashtag';
  weight: number;
  content: string;
};

/**
 * @name VerticalTag
 * @type vertical_tags
 */
export type VerticalTag = {
  id: string;
  name: string;
  displayText?: string;
};
/**
 * @name Question
 * @type text_questions
 */
export type Question = {
  id?: string;
  body?: string;
  type?: string;
};
/**
 * @name PreferredCreatorAgeDetail
 * @type preferred_creator_age_details
 */
export type PreferredCreatorAgeDetail = {
  id?: string;
  minAge?: number;
  maxAge?: number;
};
/**
 * @name PreferredCreatorGender
 * @type preferred_creator_genders
 */
export type PreferredCreatorGender = {
  id?: string;
  name?: 'Male' | 'Female' | 'Other' | string;
};
/**
 * @name PreferredCreatorState
 * @type preferred_creator_states
 */
export type PreferredCreatorState = {
  id?: string;
  name?: string;
  country?: string;
};
/**
 * @name PreferredCreatorCity
 * @type preferred_creator_cities
 */
export type PreferredCreatorCity = {
  id?: string;
  name?: string;
  city?: string;
  state?: string;
  country?: string;
};
/**
 * @name PreferredCreatorCountry
 * @type preferred_creator_countries
 */
export type PreferredCreatorCountry = {
  id?: string;
  name?: string;
};
/**
 * @name PreferredCreatorLocation
 * @type preferred_creator_locations
 */
export type PreferredCreatorLocation = {
  id?: string;
  type: LocationType;
  api_uid?: string;
  name?: string;
  longitude?: number;
  latitude?: number;
  city?: string;
  state?: string;
  country?: string;
};
/**
 * @name PreferredCreatorCapability
 * @type preferred_creator_capabilities
 */
export type PreferredCreatorCapability = {
  id?: string;
  name?: string;
};
/**
 * @name PreferredCreatorSpecialty
 * @type preferred_creator_specialties
 */
export type PreferredCreatorSpecialty = {
  id?: string;
  name?: string;
};
/**
 * @name CreatorGroup
 * @type creator_groups
 */
export type CreatorGroup = {
  id?: string;
  activeMembershipCount?: number;
  name?: string;
  totalMembershipCount?: number;
};

/**
 * @name ContentAgreementTerm
 * @type content_agreement_terms
 */
export type ContentAgreementTerm = {
  id: string;
  name: string;
  displayText: string;
};

/**
 * @name CampaignApprovalStep
 * @type campaign_approval_steps
 */
export type CampaignApprovalStep = {
  id: string;
  campaignId: string;
  step: string;
};

/**
 * @name Brand
 * @type brands
 */
export type Brand = {
  id: string;
  agency?: object;
  displayAvatarUrl?: string;
  displayName?: string;
  hasShopifyAccessToken?: boolean;
  name: string;
  shopifyShopName?: string;
  tiktokBusinessAuthorizations: any[];
};

export type SocialNetwork = {
  id: string;
  name: string;
  supportsStories?: boolean;
  supportsReels?: boolean;
};

export const CAMPAIGN_AASM_STATES = {
  UNDER_REVIEW: 'under_review',
  DRAFT: 'draft',
  CLOSED: 'closed',
  CLOSED_TO_NEW_PROPOSALS: 'closed_to_new_proposals',
  UNDER_CONSTRUCTION: 'under_construction',
  ACCEPTING_PROPOSALS: 'accepting_proposals',
} as const;

export type CampaignAasmState =
  | (typeof CAMPAIGN_AASM_STATES)[keyof typeof CAMPAIGN_AASM_STATES]
  | string;

export const ACTIVE_CAMPAIGN_STATES: CampaignAasmState[] = [
  CAMPAIGN_AASM_STATES.ACCEPTING_PROPOSALS,
  CAMPAIGN_AASM_STATES.CLOSED_TO_NEW_PROPOSALS,
];

/**
 * @name Campaign
 * @type campaigns
 */
export type Campaign = {
  id?: string;
  brandId?: string;
  affiliateType?: string | null;
  blogsToUpload?: number;
  budgetRange?: string | null;
  estimatedBudget?: number | null;
  deliverablesType?: 'shipments' | 'gift_codes' | null;
  descriptionHtml?: string | null;
  estimatedFulfillmentValue?: number | null;
  fixedCostForCreations?: number | null;
  image?: string | null;
  isDraft?: boolean;
  locksAbove?: number | null;
  minimumChildContentSubmissionCount?: number;
  mixedMediaToUpload?: number;
  name: string;
  network?: string | null;
  photosToUpload?: number;
  requiredFeedPostsCount?: number;
  requiredReelsPostsCount?: number;
  requiredStoryPostsCount?: number;
  suggestedCostForCreations?: number | null;
  suggestedCostForCreationsMin?: number | null;
  swapMatchKeywords?: string[];
  unlocksAt?: number;
  campaignTrackingUrls?: CampaignTrackingUrl[];
  videosToUpload?: number;
  state?: CampaignAasmState | null;
  aasmState?: CampaignAasmState | null;
  salesforceId?: string | null;
  offPlatformShareToken?: string | null;
  offPlatformShareTokenExpiresAt?: string | null;
  // RELATIONSHIPS
  campaignPaidGigBriefItems?: CampaignPaidGigBriefItem[];
  campaignBriefDeliverableItems?: CampaignBriefDeliverableItem[];
  campaignBriefCaptionGuidelines?: CampaignBriefCaptionGuideline[];
  verticalTags?: VerticalTag[];
  questions?: Question[];
  preferredCreatorAgeDetail?: PreferredCreatorAgeDetail;
  preferredCreatorGenders?: PreferredCreatorGender[];
  preferredCreatorStates?: PreferredCreatorState[];
  preferredCreatorCities?: PreferredCreatorCity[];
  preferredCreatorCountries?: PreferredCreatorCountry[];
  preferredCreatorCapabilities?: PreferredCreatorCapability[];
  preferredCreatorSpecialties?: PreferredCreatorSpecialty[];
  creatorGroups?: CreatorGroup[];
  contentAgreementTerms?: ContentAgreementTerm[];
  contentAgreementTimePeriodInMonths?: number | string;
  daysToPostAfterDeliverableFulfilled?: number | null;
  sectionsToHide?: Record<string, boolean>;
  generatedByBriefAi?: boolean;
  commissionRate?: string | null;
  campaignApprovalSteps?: CampaignApprovalStep[];
  brand?: Brand;
};

export interface CampaignProps extends Campaign {
  // Additional props from CampaignProps
  isProcessing?: boolean;
  isPlaceholder?: boolean;
  version?: number;
  loaded: boolean;
  id: string;
  brandId?: string;
  brand: Brand;
  imageUrl?: string | null;
  isActive: boolean;
  state: string;
  socialNetwork?: SocialNetwork;
  verticalTags: VerticalTag[];
  campaignPaidGigBriefItems: CampaignPaidGigBriefItem[];
  campaignBriefDeliverableItems: CampaignBriefDeliverableItem[];
  campaignBriefCaptionGuidelines: CampaignBriefCaptionGuideline[];
  contentAgreementTerms: ContentAgreementTerm[];
  contentAgreementTimePeriodInMonths?: string;
  creatorCompensationType?: 'fixed' | 'range';

  // Missing attributes from campaign.js
  acceptedContractsCount?: number;
  acceptedContractsSocialReach?: number;
  acceptedTotalPrice?: number;
  brandActionItemsGroup?: {
    applicationsCount?: number;
    contentSubmissionsCount?: number;
    creatorSuggestionsCount?: number;
    [key: string]: any;
  };
  budgetAmount?: number;
  budgetAmountRemaining?: number;
  captionPlaceholder?: string | null;
  createdAt?: string | Date;
  currentInviteCount?: number;
  dashboardPendingLimit?: number;
  deliverablesCount?: number;
  directLinkBriefUrl?: string | null;
  fixedPercentageMargin?: number;
  hideInfluencerPricing?: boolean;
  impressionsVisible?: boolean;
  isOpenMarket?: boolean;
  inProgressPaidGigs?: number;
  maxInviteCount?: number;
  metrics?: {
    [key: string]: any;
  };
  otherFeePercentage?: number;
  paidGigsWithPendingNegotiation?: number;
  pendingProposalsCount?: number;
  proposalsCount?: number;
  requiresPreapproval?: boolean;
  serviceFeePercentage?: number;
  shortlistedProposalsCount?: number;
  shortlistedProposalsSocialReach?: number;
  shortlistedProposalsTotalPrice?: number;
  updatedAt?: string | Date;
  unreadConversationsCount?: number;
  unreadApplicationMessagesCount?: number;
  unreadParticipantMessagesCount?: number;
  shopifyTotalSalesAmount?: number;

  // BelongsTo relationships
  adNetworkAccount?: {
    id?: string;
    [key: string]: any;
  } | null;
  bulkExport?: {
    id?: string;
    [key: string]: any;
  } | null;
  campaignContentSubmissionsMetrics?: {
    id?: string;
    [key: string]: any;
  } | null;
  campaignTimeline?: {
    id?: string;
    [key: string]: any;
  } | null;
  collective?: {
    id?: string;
    [key: string]: any;
  } | null;
  preferredCreatorProfile?: {
    id?: string;
    [key: string]: any;
  } | null;
  campaignBrief?: {
    id?: string;
    [key: string]: any;
  } | null;

  // HasMany relationships
  contentSubmissions?: any[];
  deliverables?: any[];
  proposals?: any[];
  questions?: Question[];
  socialMediaPosts?: any[];
  userGroupLabels?: any[];

  // Computed properties from useCampaignQuery
  showCampaignDetails: boolean;
  networkDasherized?: string | null;
  isBcaCompatible: boolean;
  campaignNetworkPrettyName?: string | null;
  campaignType: string;
  invitesRemaining: number;
  validForActionItems: boolean;
  validForInviteActionItem: boolean;
  totalFeePercentage: number;
  desiredStatsSocialStream: 'feed' | 'story' | 'reels';
  hasFulfillment: boolean;
  hasRemainingInvites: boolean;
  noAdManager: boolean;
  isMarginBased: boolean;
  isUnderReview: boolean;
  hasUnreadConversations: boolean;
  blogUploadRequired: boolean;
  mixedMediaUploadRequired: boolean;
  photoPostRequired: boolean;
  reelsPostRequired: boolean;
  storyPostRequired: boolean;
  photoUploadRequired: boolean;
  videoUploadRequired: boolean;
}

export type CampaignDeliverableCounters =
  | 'requiredStoryPostsCount'
  | 'requiredFeedPostsCount'
  | 'requiredReelsPostsCount'
  | 'videosToUpload'
  | 'photosToUpload';

export type RequiredDeliverableTypeOptions =
  | 'feed_post_photo'
  | 'reel_post'
  | 'story_post'
  | 'tiktok_video'
  | 'youtube_short_form_video'
  | 'youtube_long_form_video'
  | 'youtube_short'
  | 'content_standard_photo'
  | 'content_standard_video'
  | 'content_premium_photo';

export type RequiredDeliverable = {
  id?: string;
  type: RequiredDeliverableTypeOptions;
  description: string;
};

export type BriefData = {
  is_processing?: boolean;
  is_placeholder?: boolean;
  version?: number;
  logo_url?: string | null;
  campaign_name?: string | null;
  network?: string | null;
  about_your_brand_and_campaign?: string | null;
  campaign_hashtag?: string | null | undefined;
  creator_cost_range?: string | null;
  creator_gender?: string[] | null;
  creator_followers_count?: string | null;
  ideal_creator?: string | null;
  creator_age_range?: string | null;
  campaign_verticals?: string[] | null;
  product_fulfillment?:
    | {
        method?: string | null;
        cash_value?: number | null;
      }
    | undefined;
  product_to_feature?: string | null;
  required_deliverables?: RequiredDeliverable[] | null;
  content_mandatories?: string | null;
  posting_instructions?: string | null;
  days_to_post_after_deliverable_fulfilled?: number | null;
  time_period_months?: string | number | null;
  content_agreement_terms?: ContentAgreementTerm[];
  campaign_brief_caption_guidelines?: CampaignBriefCaptionGuideline[];
  creator_compensation_type?: 'fixed' | 'range';
  commission_rate?: number | null;
};

export type RequiredDeliverableCounts = {
  feed_post_photo?: number | null;
  reel_post?: number | null;
  story_post?: number | null;
  story_post_number_of_frames?: number | null;
  tiktok_video?: number | null;
  youtube_short_form_video?: number | null;
  youtube_long_form_video?: number | null;
  youtube_short?: number | null;
  content_standard_photo?: number | null;
  content_standard_video?: number | null;
  content_premium_photo?: number | null;
  content_premium_video?: number | null;
};

export type Location = {
  id: string;
  type: LocationType;
  api_uid: string;
  longitude: number;
  latitude: number;
  city?: string;
  state?: string;
  country?: string;
};

export type AgeRange = {
  from_age: number;
  to_age?: number;
};

export type Gender = 'male' | 'female' | 'other';

export type Specialty = {
  id: string;
  name: string;
};

export type WizardFormData = {
  brand_name?: string | null;
  campaign_guidelines?: string | null;
  budget: number;
  network?: string | null;
  genders?: Gender[] | null;
  age_range?: AgeRange | null;
  locations?: Location[] | null;
  specialties?: Specialty[] | null;
  creator_cost_range?: string | null;
  creator_followers_count?: string | null;
  required_deliverables: RequiredDeliverableCounts & {
    story_post_number_of_frames?: number | null;
  };
  product_fulfillment?:
    | {
        method?: string | null;
        cash_value?: number | null;
      }
    | undefined;
};
